/* The main couple-signup form. Extracted from CoupleSignup.jsx purely to
   keep the page component small and the per-section JSX scannable.
   All state remains owned by the parent — this component is a pure view. */
import { Send } from "lucide-react";
import SlotPicker from "@/components/SlotPicker";
import {
  Field,
  TextInput,
  TextArea,
  Checkboxes,
  Radios,
} from "@/components/form-fields";
import {
  WEDDING_TYPE_OPTIONS,
  SERVICES_OPTIONS,
  COMMUNICATION_OPTIONS,
  VIBE_OPTIONS,
  HEARD_OPTIONS,
} from "@/components/couple-signup/constants";

export const CoupleSignupForm = ({
  form,
  setForm,
  set,
  onSubmit,
  submitting,
  onSaveDraft,
  savingDraft,
  draftLastSavedAt,
}) => (
          <form
            data-testid="couple-signup-form"
            onSubmit={onSubmit}
            className="mt-12 space-y-12"
          >
            {/* Section 1 — About you */}
            <section className="space-y-6">
              <h3 className="font-serif-display text-2xl text-[var(--ld-ink)]">
                About the couple
              </h3>
              <Field
                label="Full name of couple"
                required
                testId="q-couple-name"
              >
                <TextArea
                  id="couple_full_name"
                  value={form.couple_full_name}
                  onChange={set("couple_full_name")}
                  rows={2}
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field
                  label="Email address (best to contact you on)"
                  required
                  hint="Linked to your sign-in. Contact us if this needs to change."
                  testId="q-email"
                >
                  <TextInput
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    readOnly
                  />
                </Field>
                <Field
                  label="Mobile number"
                  required
                  hint="Please state groom/bride number."
                  testId="q-mobile"
                >
                  <TextInput
                    id="mobile_number"
                    type="tel"
                    value={form.mobile_number}
                    onChange={set("mobile_number")}
                  />
                </Field>
              </div>
            </section>

            {/* Section 2 — Wedding details */}
            <section className="space-y-6">
              <h3 className="font-serif-display text-2xl text-[var(--ld-ink)]">
                Your wedding
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field
                  label="Date of the wedding"
                  required
                  testId="q-date"
                >
                  <TextInput
                    id="wedding_date"
                    type="date"
                    value={form.wedding_date}
                    onChange={set("wedding_date")}
                  />
                </Field>
                <Field
                  label="How many guests are you expecting?"
                  required
                  testId="q-guests"
                >
                  <TextInput
                    id="guest_count"
                    type="number"
                    value={form.guest_count}
                    onChange={set("guest_count")}
                    placeholder="e.g. 250"
                  />
                </Field>
              </div>
              <Field
                label="Venue and address of venue"
                required
                testId="q-venue"
              >
                <TextArea
                  id="venue_address"
                  value={form.venue_address}
                  onChange={set("venue_address")}
                  rows={3}
                />
              </Field>
              <Field
                label="Does your venue have any restrictions or requirements?"
                required
                testId="q-restrictions"
              >
                <TextArea
                  id="venue_restrictions"
                  value={form.venue_restrictions}
                  onChange={set("venue_restrictions")}
                  rows={3}
                  placeholder="e.g. open flame, sound limits, supplier list, etc."
                />
              </Field>
              <Field
                label="What is your social media (Instagram / TikTok) handle?"
                required
                hint="The purpose is to promote any social content if you choose any of the vendors."
                testId="q-socials"
              >
                <TextArea
                  id="social_handles"
                  value={form.social_handles}
                  onChange={set("social_handles")}
                  rows={2}
                />
              </Field>
              <Field label="What is your overall budget?" required testId="q-budget">
                <TextArea
                  id="budget"
                  value={form.budget}
                  onChange={set("budget")}
                  rows={2}
                  placeholder="e.g. £30,000 — £45,000"
                />
              </Field>
            </section>

            {/* Section 3 — Style & services */}
            <section className="space-y-6">
              <h3 className="font-serif-display text-2xl text-[var(--ld-ink)]">
                Style & services
              </h3>
              <Field
                label="What does your wedding look like?"
                required
                hint="Select all that apply."
                testId="q-wedding-type"
              >
                <Checkboxes
                  id="wedding_type"
                  options={WEDDING_TYPE_OPTIONS}
                  value={form.wedding_type}
                  onChange={set("wedding_type")}
                />
              </Field>
              <Field
                label="What services do you need support finding?"
                required
                hint="Select all that apply."
                testId="q-services"
              >
                <Checkboxes
                  id="services_needed"
                  options={SERVICES_OPTIONS}
                  value={form.services_needed}
                  onChange={set("services_needed")}
                />
              </Field>
              <Field
                label="How would you describe the overall vibe you want?"
                required
                testId="q-vibe"
              >
                <Radios
                  id="overall_vibe"
                  options={VIBE_OPTIONS}
                  value={form.overall_vibe}
                  onChange={set("overall_vibe")}
                />
              </Field>
            </section>

            {/* Section 4 — Priorities */}
            <section className="space-y-6">
              <h3 className="font-serif-display text-2xl text-[var(--ld-ink)]">
                Priorities & preferences
              </h3>
              <Field
                label="Which aspects of your wedding are the highest priority for you?"
                required
                testId="q-priorities"
              >
                <TextArea
                  id="priorities"
                  value={form.priorities}
                  onChange={set("priorities")}
                  rows={4}
                />
              </Field>
              <Field
                label="Are there any absolute must-haves or deal-breakers?"
                required
                testId="q-must"
              >
                <TextArea
                  id="must_haves"
                  value={form.must_haves}
                  onChange={set("must_haves")}
                  rows={4}
                />
              </Field>
              <Field
                label="How do you prefer to communicate with vendors?"
                required
                testId="q-comm"
              >
                <Radios
                  id="communication_preference"
                  options={COMMUNICATION_OPTIONS}
                  value={form.communication_preference}
                  onChange={set("communication_preference")}
                />
              </Field>
            </section>

            {/* Section 5 — A few last things */}
            <section className="space-y-6">
              <h3 className="font-serif-display text-2xl text-[var(--ld-ink)]">
                A few last things
              </h3>
              <Field
                label="Any vendors you've already booked? If so, who?"
                required
                hint="Write 'None' if you haven't booked anyone yet."
                testId="q-booked"
              >
                <TextArea
                  id="vendors_already_booked"
                  value={form.vendors_already_booked}
                  onChange={set("vendors_already_booked")}
                  rows={3}
                />
              </Field>
              <Field
                label="Is there anything else you'd like us to know?"
                required
                hint="Write 'No' or 'N/A' if nothing comes to mind."
                testId="q-anything-else"
              >
                <TextArea
                  id="anything_else"
                  value={form.anything_else}
                  onChange={set("anything_else")}
                  rows={3}
                />
              </Field>
              <Field
                label="How did you hear about us?"
                required
                testId="q-heard"
              >
                <Radios
                  id="heard_about_us"
                  options={HEARD_OPTIONS}
                  value={form.heard_about_us}
                  onChange={set("heard_about_us")}
                />
              </Field>
              <Field
                label="Book your introduction appointment"
                hint="Pick a slot for an introductory chat with our team. Once chosen, the time will be reserved exclusively for you."
                testId="q-intro-slot"
              >
                <p
                  data-testid="slot-availability-note"
                  className="mb-4 text-[13px] leading-[1.7] text-[var(--ld-ink)]/85 bg-[var(--ld-gold-soft)]/10 border-l-2 border-[var(--ld-gold-deep)] pl-3 py-2"
                >
                  If there are no consultation slots available, leave this
                  blank — I will contact you to arrange one. Either a chosen
                  slot or our follow-up call is compulsory before approval.
                </p>
                <SlotPicker
                  role="couple"
                  testId="couple-slot-picker"
                  value={
                    form.intro_slot_date && form.intro_slot_time
                      ? {
                          date: form.intro_slot_date,
                          time: form.intro_slot_time,
                        }
                      : null
                  }
                  onChange={(next) => {
                    setForm((prev) => ({
                      ...prev,
                      intro_slot_date: next?.date || "",
                      intro_slot_time: next?.time || "",
                    }));
                  }}
                />
              </Field>
            </section>

            {/* Declaration */}
            <section
              data-testid="declaration-section"
              className="bg-white/40 border border-[var(--ld-border)] p-5 md:p-7"
            >
              <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-[var(--ld-gold-deep)] mb-3">
                Declaration
              </p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="declaration-checkbox"
                  checked={form.declaration_confirmed}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      declaration_confirmed: e.target.checked,
                    }))
                  }
                  className="mt-1 h-4 w-4 accent-[var(--ld-ink)]"
                />
                <span className="text-[14px] leading-[1.7] text-[var(--ld-ink)]/85">
                  I confirm all the information I have input are correct. An
                  email will be sent with the introduction appointment details.
                  I have read everything and completed it to the best of my
                  knowledge.
                </span>
              </label>
            </section>

            <div className="flex flex-col items-center gap-3 pt-4">
              {draftLastSavedAt && (
                <p
                  data-testid="couple-draft-status"
                  className="text-[11px] tracking-[0.18em] uppercase text-[var(--ld-gold-deep)]"
                >
                  Draft saved · {new Date(draftLastSavedAt).toLocaleTimeString()}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-center gap-3">
                {onSaveDraft && (
                  <button
                    type="button"
                    onClick={onSaveDraft}
                    disabled={savingDraft}
                    data-testid="couple-save-draft"
                    className="inline-flex items-center justify-center gap-2 px-6 py-4 text-[12px] tracking-[0.2em] uppercase bg-transparent text-[var(--ld-ink)] border border-[var(--ld-ink)] transition-colors duration-500 hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {savingDraft ? "Saving draft…" : "Save draft"}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={submitting || !form.declaration_confirmed}
                  data-testid="couple-submit"
                  className="inline-flex items-center justify-center gap-2 px-10 py-4 text-[12px] tracking-[0.2em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] border border-[var(--ld-ink)] transition-colors duration-500 hover:text-[#e9d59a] hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send size={14} strokeWidth={1.6} />
                  {submitting ? "Submitting…" : "Submit"}
                </button>
              </div>
            </div>
          </form>
);
